// --- REWRITE START ---
import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, Toaster } from 'react-hot-toast';
import {
  ShoppingCartIcon,
  UserIcon,
  HeartIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
  EyeIcon,
  StarIcon,
  CheckIcon,
  CreditCardIcon,
  TruckIcon,
  ShieldCheckIcon,
  PhotoIcon,
  CloudArrowUpIcon,
  ArrowRightIcon,
  ShoppingBagIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  HomeIcon,
  ArrowRightStartOnRectangleIcon,
  Squares2X2Icon,
  TagIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import logo from './images/FINAL-DESIGN-LOGO.png';
import {
  HeartIcon as HeartSolidIcon,
  StarIcon as StarSolidIcon,
  CheckCircleIcon
} from '@heroicons/react/24/solid';
import axios from 'axios';
import AdminDashboard from './components/admin/AdminDashboard';
import ProductsPage from './pages/ProductsPage';
import TemplateBuilder from './components/admin/template-builder/TemplateBuilder';
import ProfilePage from './pages/ProfilePage';
import './App.css';
// Removed HeroCarousel import
import CircleAnime from './CircleAnime';
import ProductCustomizer from './components/customizer/ProductCustomizer';
import ProductDetailPage from './ProductDetailPage';
import Footer from './components/Footer';

import { API } from './config';
import Navigation from './components/Navigation';
import AboutSection from './components/AboutSection';
import BlogPage from './pages/BlogPage';
import BlogPostDetail from './pages/BlogPostDetail';
import { AppProvider, useAppContext } from './context/AppContext';


// ...existing code...


// Initial full-height dark background section
// Initial full-height dark background section
const HeroSection = ({ bannerUrl }) => {
  return (
    <div
      style={{
        height: '100vh',
        width: '100vw',
        background: bannerUrl
          ? `url(${bannerUrl}) center center / cover no-repeat`
          : '#111114',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      {/* Optionally overlay a dark gradient for readability */}
      {bannerUrl && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)',
          zIndex: 1,
        }} />
      )}
      {/* Content can be added here later */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        {/* Add hero text, logo, etc. here if needed */}
      </div>
    </div>
  );
};


// Enhanced Product Detail Modal
// Product Card Component
const ProductCard = ({ product }) => {
  const [isLiked, setIsLiked] = useState(false);
  const { addToCart } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    setIsLoading(true);
    // Default to adding 1 quantity if no size needed, else navigate to product page
    if (product.sizes && product.sizes.length > 0) {
      navigate(`/product/${product.id}`);
      setIsLoading(false);
      return;
    }
    await addToCart(product.id, 1);
    setIsLoading(false);
  };

  const handleCardClick = () => {
    navigate(`/product/${product.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -8 }}
      onClick={handleCardClick}
      className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 cursor-pointer"
    >
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        <img
          src={product.images?.[0] || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab'}
          alt={product.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />

        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {product.is_customizable && (
            <span className="bg-white/90 backdrop-blur-sm text-gray-900 text-xs font-bold px-3 py-1 rounded-full shadow-sm">
              Customizable
            </span>
          )}
          {product.is_new && (
            <span className="bg-gray-900 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
              New
            </span>
          )}
        </div>
      </div>

      <div className="p-4 text-center">
        <h3 className="font-bold text-gray-900 text-lg leading-tight group-hover:text-gray-600 transition-colors mb-2">
          {product.title}
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          {product.description?.replace(/<[^>]*>/g, '') || 'Fresh and premium quality'}
        </p>
      </div>
    </motion.div>
  );
};
const ProductDetailModal = ({ product, isOpen, onClose }) => {
  const { addToCart } = useAppContext();
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);

  // Reset selectedSize when product changes
  useEffect(() => {
    setSelectedSize('');
  }, [product]);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen || !product) return null;

  const handleAddToCart = async () => {
    if (product.sizes && product.sizes.length > 0 && !selectedSize) {
      toast.error('Please select a size before adding to cart.');
      return;
    }
    setIsLoading(true);
    await addToCart(product.id, quantity, selectedSize);
    setIsLoading(false);
    onClose();
  };
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white w-screen h-screen max-w-none max-h-none overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button at top right of modal screen */}
          <button
            onClick={onClose}
            className="absolute top-6 right-8 bg-white/90 backdrop-blur-sm p-2 hover:bg-white transition-colors z-50"
            style={{ position: 'fixed' }}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          {/* Responsive layout: mobile first */}
          <div className="block lg:hidden p-4">
            {/* Product Images */}
            <div className="mb-6">
              <div className="grid grid-cols-2 gap-4">
                {product.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={product.title}
                    loading="lazy"
                    className="w-full"
                  />
                ))}
              </div>
            </div>
            {/* Title and Description */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{product.title}</h2>
              <div
                className="text-gray-600 text-sm leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: product.description
                    ? product.description.replace(/(?:\r\n|\r|\n)/g, '<br>')
                    : ''
                }}
              />
            </div>
            {/* Rest of Details */}
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <span className="text-2xl font-bold text-[#362222]">₹{product.price}</span>
              </div>
              <div className="flex items-center space-x-1 mt-2">
                {[...Array(5)].map((_, i) => (
                  <StarSolidIcon key={i} className="h-5 w-5 text-yellow-400" />
                ))}
                <span className="text-gray-500 ml-2">(4.8 rating)</span>
              </div>
              {/* Size Selection */}
              {product.sizes && product.sizes.length > 0 && (
                <div className="space-y-1">
                  <h4 className="font-semibold text-lg mb-1">Size:</h4>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.sizes.map((size) => (
                      <motion.button
                        key={size}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setSelectedSize(size)}
                        className={`px-4 py-2 border text-sm rounded-md font-medium transition-all ${selectedSize === size
                          ? 'border-[#E31E24] bg-white text-[#E31E24] shadow-lg'
                          : 'border-gray-300 hover:border-[#E31E24] hover:shadow-md'
                          }`}
                      >
                        {size}
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}
              {/* Quantity Selection */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg mb-1">Quantity:</h4>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 border-2 border-gray-300 rounded-xl hover:border-[#E31E24] hover:bg-gray-50 transition-colors"
                  >
                    <MinusIcon className="h-3 w-3" />
                  </button>
                  <span className="w-12 text-center font-medium text-lg">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 border-2 border-gray-300 rounded-xl hover:border-[#E31E24] hover:bg-gray-50 transition-colors"
                  >
                    <PlusIcon className="h-3 w-3" />
                  </button>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="space-y-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAddToCart}
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-[#E31E24] to-[#C01A1F] text-white py-2 rounded-md text-base font-semibold hover:from-[#C01A1F] hover:to-[#A01619] transition-all duration-300 shadow-lg disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      <span>Adding...</span>
                    </div>
                  ) : (
                    'Add to Cart'
                  )}
                </motion.button>
                <button className="w-full border-2 border-[#E31E24] text-[#E31E24] py-2 rounded-md text-base font-semibold hover:bg-[#E31E24] hover:text-white transition-all duration-300">
                  Add to Wishlist
                </button>
              </div>
              {/* Product Features */}
              {product.is_customizable && (
                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-orange-100 p-2 rounded-lg">
                      <PhotoIcon className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-orange-800 mb-2">Customization Available</h4>
                      <p className="text-sm text-orange-700">
                        This product can be personalized with your own design. Upload your image during checkout for a truly unique creation.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                <div className="text-center space-y-2">
                  <ShieldCheckIcon className="h-8 w-8 text-green-500 mx-auto" />
                  <span className="text-sm font-medium text-gray-700">Quality Guaranteed</span>
                </div>
                <div className="text-center space-y-2">
                  <TruckIcon className="h-8 w-8 text-blue-500 mx-auto" />
                  <span className="text-sm font-medium text-gray-700">Fast Shipping</span>
                </div>
                <div className="text-center space-y-2">
                  <CheckCircleIcon className="h-8 w-8 text-purple-500 mx-auto" />
                  <span className="text-sm font-medium text-gray-700">Easy Returns</span>
                </div>
              </div>
            </div>
          </div>
          {/* Desktop layout: unchanged */}
          <div className="hidden lg:grid grid-cols-[3fr_3fr_1.5fr] gap-8 p-8">
            {/* Product Images */}
            <div className="space-y-4">
              <div className="relative overflow-hidden">
                <div className="grid grid-cols-2 gap-4">
                  {product.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={product.title}
                      className="w-full"
                    />
                  ))}
                </div>
              </div>
            </div>
            {/* Product Details */}
            <div className="space-y-4">
              <div>
                <h2 className="text-4xl font-bold text-gray-800 mb-4">{product.title}</h2>
                <div
                  className="text-gray-600 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: product.description
                      ? product.description.replace(/(?:\r\n|\r|\n)/g, '<br>')
                      : ''
                  }}
                />
              </div>
              {/* Product Features */}
              {product.is_customizable && (
                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-orange-100 p-2 rounded-lg">
                      <PhotoIcon className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-orange-800 mb-2">Customization Available</h4>
                      <p className="text-sm text-orange-700">
                        This product can be personalized with your own design. Upload your image during checkout for a truly unique creation.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                <div className="text-center space-y-2">
                  <ShieldCheckIcon className="h-8 w-8 text-green-500 mx-auto" />
                  <span className="text-sm font-medium text-gray-700">Quality Guaranteed</span>
                </div>
                <div className="text-center space-y-2">
                  <TruckIcon className="h-8 w-8 text-blue-500 mx-auto" />
                  <span className="text-sm font-medium text-gray-700">Fast Shipping</span>
                </div>
                <div className="text-center space-y-2">
                  <CheckCircleIcon className="h-8 w-8 text-purple-500 mx-auto" />
                  <span className="text-sm font-medium text-gray-700">Easy Returns</span>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-4">
                <span className="text-4xl font-bold text-[#362222]">₹{product.price}</span>
              </div>
              <div className="flex items-center space-x-1 mt-2">
                {[...Array(5)].map((_, i) => (
                  <StarSolidIcon key={i} className="h-5 w-5 text-yellow-400" />
                ))}
                <span className="text-gray-500 ml-2">(4.8 rating)</span>
              </div>
              {/* Size Selection */}
              {product.sizes && product.sizes.length > 0 && (
                <div className="space-y-1">
                  <h4 className="font-semibold text-lg mb-1">Size:</h4>
                  <div className="flex flex-wrap gap-2 mb-12">
                    {product.sizes.map((size) => (
                      <motion.button
                        key={size}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setSelectedSize(size)}
                        className={`px-4 py-2 border text-sm rounded-md font-medium transition-all ${selectedSize === size
                          ? 'border-[#E31E24] bg-white text-[#E31E24] shadow-lg'
                          : 'border-gray-300 hover:border-[#E31E24] hover:shadow-md'
                          }`}
                      >
                        {size}
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}
              {/* Quantity Selection */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg mb-1">Quantity:</h4>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 border-2 border-gray-300 rounded-xl hover:border-[#E31E24] hover:bg-gray-50 transition-colors"
                  >
                    <MinusIcon className="h-3 w-3" />
                  </button>
                  <span className="w-12 text-center font-medium text-lg">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 border-2 border-gray-300 rounded-xl hover:border-[#E31E24] hover:bg-gray-50 transition-colors"
                  >
                    <PlusIcon className="h-3 w-3" />
                  </button>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="space-y-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAddToCart}
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-[#E31E24] to-[#C01A1F] text-white py-2 rounded-md text-base font-semibold hover:from-[#C01A1F] hover:to-[#A01619] transition-all duration-300 shadow-lg disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      <span>Adding...</span>
                    </div>
                  ) : (
                    'Add to Cart'
                  )}
                </motion.button>
                <button className="w-full border-2 border-[#E31E24] text-[#E31E24] py-2 rounded-md text-base font-semibold hover:bg-[#E31E24] hover:text-white transition-all duration-300">
                  Add to Wishlist
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Enhanced Home Page Component
// Enhanced Home Page Component
const HomePage = () => {
  const location = useLocation();
  const [newArrivals, setNewArrivals] = useState([]);
  const [sections, setSections] = useState([]);
  const [heroImages, setHeroImages] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Carousel state and handlers
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    fetchNewArrivals();
    fetchSections();
    fetchHeroImages();
    fetchLocations();
  }, []);

  useEffect(() => {
    if (location.state?.scrollToAbout) {
      const element = document.getElementById('about-us');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        // Clear state to prevent scrolling on every render
        window.history.replaceState({}, document.title);
      }
    }
  }, [location]);

  // Auto-slide for carousel
  useEffect(() => {
    let interval;
    if (heroImages.length > 1) {
      interval = setInterval(() => {
        setHeroIndex(prev => (prev + 1) % heroImages.length);
      }, 4000); // 4 seconds
    }
    return () => clearInterval(interval);
  }, [heroImages.length]);

  // Navigation handlers
  const handlePrev = () => {
    setHeroIndex(prev => (prev - 1 + heroImages.length) % heroImages.length);
  };
  const handleNext = () => {
    setHeroIndex(prev => (prev + 1) % heroImages.length);
  };

  const fetchNewArrivals = async () => {
    try {
      const response = await axios.get(`${API}/products?is_new_arrival=true`);
      setNewArrivals(response.data);
    } catch (error) {
      console.error('Failed to fetch new arrivals:', error);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API}/sections`);
      setSections(response.data);
    } catch (error) {
      console.error('Failed to fetch sections:', error);
    }
  };

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(`${API}/hero-images`);
      setHeroImages(response.data);
    } catch (error) {
      console.error('Failed to fetch hero images:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };



  // Get hero images from heroImages state
  const desktopBanners = heroImages.map(img => img.desktop_url).filter(url => !!url && typeof url === 'string');
  const mobileBanners = heroImages.map(img => img.mobile_url).filter(url => !!url && typeof url === 'string');

  return (
    <div className="min-h-screen" style={{ overflowX: 'hidden' }}>
      {/* Custom Hero Section Logic */}
      <div
        className="w-full relative mt-[80px] md:mt-[80px]"
        style={{ minHeight: '60vh', overflowX: 'hidden', marginBottom: '0', paddingBottom: '0' }}
      >
        {/* Desktop: Single image or carousel */}
        <div className="hidden md:flex w-full h-[60vh] items-center justify-center scrollbar-hide" style={{ overflowX: 'hidden', position: 'relative' }}>
          {desktopBanners.length === 1 && (
            <img
              src={desktopBanners[0]}
              alt="Hero desktop banner"
              className="flex-shrink-0 h-full object-cover"
              style={{ maxWidth: '100vw', width: '100vw', minHeight: '60vh' }}
            />
          )}
          {desktopBanners.length > 1 && (
            <AnimatePresence mode="wait">
              <motion.img
                key={heroIndex}
                src={desktopBanners[heroIndex % desktopBanners.length]}
                alt="Hero desktop banner"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.6 }}
                className="flex-shrink-0 h-full object-cover"
                style={{ maxWidth: '100vw', width: '100vw', minHeight: '60vh', display: 'block' }}
              />
              {/* Arrows */}
              <button
                onClick={handlePrev}
                style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.2)', border: 'none', borderRadius: '50%', padding: 8, zIndex: 2, cursor: 'pointer' }}
                aria-label="Previous hero image"
              >
                <svg width="32" height="32" fill="none" viewBox="0 0 32 32"><circle cx="16" cy="16" r="16" fill="transparent" /><path d="M20 24l-8-8 8-8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              <button
                onClick={handleNext}
                style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.2)', border: 'none', borderRadius: '50%', padding: 8, zIndex: 2, cursor: 'pointer' }}
                aria-label="Next hero image"
              >
                <svg width="32" height="32" fill="none" viewBox="0 0 32 32"><circle cx="16" cy="16" r="16" fill="transparent" /><path d="M12 8l8 8-8 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </AnimatePresence>
          )}
        </div>
        {/* Mobile: Single image or carousel */}
        <div className="flex md:hidden w-full h-[75vh] items-center justify-center scrollbar-hide" style={{ overflowX: 'hidden', position: 'relative' }}>
          {mobileBanners.length === 1 && (
            <img
              src={mobileBanners[0]}
              alt="Hero mobile banner"
              className="flex-shrink-0 h-full object-cover"
              style={{ maxWidth: '100vw', width: '100vw', minHeight: '75vh' }}
            />
          )}
          {mobileBanners.length > 1 && (
            <AnimatePresence mode="wait">
              <motion.img
                key={heroIndex}
                src={mobileBanners[heroIndex % mobileBanners.length]}
                alt="Hero mobile banner"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.6 }}
                className="flex-shrink-0 h-full object-cover"
                style={{ maxWidth: '100vw', width: '100vw', minHeight: '75vh', display: 'block' }}
              />
              {/* Arrows */}
              <button
                onClick={handlePrev}
                style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.2)', border: 'none', borderRadius: '50%', padding: 6, zIndex: 2, cursor: 'pointer' }}
                aria-label="Previous hero image"
              >
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="transparent" /><path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              <button
                onClick={handleNext}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.2)', border: 'none', borderRadius: '50%', padding: 6, zIndex: 2, cursor: 'pointer' }}
                aria-label="Next hero image"
              >
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="transparent" /><path d="M9 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* Dynamic About Us Section */}
      <AboutSection />

      {/* Sections / Collections */}
      {sections.map((section, index) => (
        <section key={section.id} className="py-24 border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-12 items-center mb-16">
              <div className={`order-2 ${index % 2 === 0 ? 'md:order-1' : 'md:order-2'} md:col-span-2`}>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
                <p className="text-base text-gray-600 whitespace-pre-line">{section.description}</p>
              </div>
              <div className={`order-1 ${index % 2 === 0 ? 'md:order-2' : 'md:order-1'} md:col-span-1 aspect-[3/4] rounded-3xl overflow-hidden shadow-lg`}>
                <img
                  src={section.desktop_banner_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8'}
                  alt={section.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {section.product_ids.slice(0, 4).map((pid) => (
                <SectionProductCard key={pid} productId={pid} asProductCard />
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* New Arrivals Section */}
      {newArrivals.length > 0 && (
        <section id="products-section" className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4 tracking-tight">Our Products</h2>
              <p className="text-base text-gray-600 max-w-2xl mx-auto">
                Carefully sourced, hygienically processed, and freshness assured.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
              {newArrivals.map((product, index) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

          </div>
        </section>
      )}

      {/* Locations Section */}
      {locations.length > 0 && (
        <section id="locations" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4 tracking-tight">Visit Our Store</h2>
              <p className="text-base text-gray-600 max-w-2xl mx-auto">
                Find us at these locations and experience freshness firsthand.
              </p>
            </motion.div>

            <div className={`grid gap-8 ${locations.length === 1 ? 'max-w-6xl mx-auto md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
              {locations.map((loc) => (
                <motion.div
                  key={loc.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="bg-gray-50 rounded-3xl p-8 border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col"
                >
                  <div className="flex flex-col h-full">
                    <div className="mb-6">
                      <div className="bg-white w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm mb-4">
                        <MapPinIcon className="h-6 w-6 text-gray-900" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{loc.name}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed mb-4">{loc.address}</p>
                      {loc.phone && (
                        <div className="flex items-center text-gray-600 text-sm mb-4">
                          <PhoneIcon className="h-4 w-4 mr-2" />
                          <span>{loc.phone}</span>
                        </div>
                      )}
                    </div>
                    {loc.embed_link && loc.embed_link.includes('google.com/maps/embed') && (
                      <div className="mb-6 rounded-2xl overflow-hidden border border-gray-200 aspect-video shadow-inner">
                        <iframe
                          src={loc.embed_link}
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title={`Map of ${loc.name}`}
                        ></iframe>
                      </div>
                    )}
                    <div className="mt-auto">
                      <a
                        href={loc.google_maps_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 bg-gray-900 text-white rounded-xl text-sm font-bold hover:bg-black transition-all shadow-sm group w-full justify-center"
                      >
                        Get Directions
                        <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

// Helper: Display product info for a section product
const SectionProductCard = ({ productId, asProductCard }) => {
  const [product, setProduct] = useState(null);
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
      } catch (error) {
        setProduct(null);
      }
    };
    fetchProduct();
  }, [productId]);
  if (!product) return null;
  if (asProductCard) {
    return <ProductCard product={product} />;
  }
  return (
    <div className="border rounded-lg p-3 flex flex-col items-center">
      <img src={product.images?.[0] || ''} alt={product.title} className="h-16 w-16 object-cover mb-2 rounded" />
      <div className="font-semibold text-gray-800 text-center text-sm">{product.title}</div>
      <div className="text-xs text-gray-500">₹{product.price}</div>
    </div>
  );
};




const CartPage = () => {
  const { cart, removeFromCart, updateCartItemQuantity, fetchCart, user, logout } = useAppContext();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // Checkout State
  const [allCoupons, setAllCoupons] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    billingAddress: '',
    phone: '',
    couponCode: '',
    shippingOption: 'standard',
    paymentMethod: 'cod',
    pincode: '',
  });
  const [products, setProducts] = useState([]);
  const [customImages, setCustomImages] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    fetchCart();
    fetchCategories();
    fetchCoupons();
    fetchRelatedProducts();

    // Load Razorpay
    if (!window.Razorpay) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (cart.length > 0) {
      fetchProductDetails();
    }
  }, [cart]);

  // Pre-fill user data
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        billingAddress: user.address || '',
        phone: user.phone || ''
      }));
    }
  }, [user]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/coupons`);
      setAllCoupons(response.data || []);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    }
  };

  const fetchRelatedProducts = async () => {
    try {
      // Just fetching some random products for now as "related"
      const response = await axios.get(`${API}/products?limit=4`);
      setRelatedProducts(response.data.slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch related products:', error);
    }
  };

  const fetchProductDetails = async () => {
    try {
      const productIds = [...new Set(cart.map(item => item.product_id))];
      const productPromises = productIds.map(id => axios.get(`${API}/products/${id}`));
      const responses = await Promise.all(productPromises);
      setProducts(responses.map(response => response.data));
    } catch (error) {
      console.error('Failed to fetch product details:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleCustomImageUpload = (cartItemId, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCustomImages(prev => ({
          ...prev,
          [cartItemId]: e.target.result
        }));
        toast.success('Custom image uploaded! 🎨');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCustomImage = (cartItemId) => {
    setCustomImages(prev => {
      const newImages = { ...prev };
      delete newImages[cartItemId];
      return newImages;
    });
    toast.success('Custom image removed');
  };

  const uploadCustomImagesToServer = async () => {
    // Get custom designs from localStorage (already uploaded to S3)
    const customDesigns = JSON.parse(localStorage.getItem('customDesigns') || '{}');

    // Simply return the existing S3 URLs - images were uploaded when adding to cart
    const results = cart.map((item) => {
      const itemDesigns = customDesigns[item.id] || [];
      return { cartItemId: item.id, designUrls: itemDesigns };
    });

    return results;
  };

  // Calculations
  const adminCouponsList = allCoupons.filter(c => c.coupon_user === 'Admin' && c.status === 'active');
  const appliedCoupon = allCoupons.find(c => c.coupon_name === formData.couponCode);

  const getSubtotal = () => cart.reduce((total, item) => total + (item.product_price * item.quantity), 0);

  const getShippingCost = () => {
    const totalQuantity = cart.reduce((total, item) => total + item.quantity, 0);
    if (totalQuantity === 0) return 0;
    const cost = 100 + (totalQuantity - 1) * 50;
    return Math.min(cost, 400);
  };

  const getCouponDiscount = () => {
    if (!appliedCoupon) return 0;
    const subtotal = getSubtotal();
    if (typeof appliedCoupon.min_purchase !== 'undefined' && subtotal < Number(appliedCoupon.min_purchase)) {
      return 0;
    }
    if (appliedCoupon.type === 'percentage') {
      return Math.round(subtotal * (appliedCoupon.discount / 100));
    } else {
      return Math.round(appliedCoupon.discount);
    }
  };

  const getTax = () => Math.round(getSubtotal() * 0.18);
  const getTotal = () => getSubtotal() + getShippingCost() + getTax() - getCouponDiscount();

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to place order');
      navigate('/login');
      return;
    }
    if (cart.length === 0) return;

    if (!formData.pincode || formData.pincode.trim() === '') {
      toast.error('Please enter your pincode');
      return;
    }

    setIsProcessing(true);
    setUploadingImages(true);
    const token = localStorage.getItem('token');

    try {
      const uploadedImages = await uploadCustomImagesToServer();
      const updatedCartItems = cart.map(item => {
        const designData = uploadedImages.find(img => img.cartItemId === item.id);
        return { ...item, custom_designs: designData?.designUrls || [] };
      });

      if (formData.paymentMethod === 'online') {
        const orderAmount = getTotal();
        const razorpayOrderRes = await axios.post(`${API}/create-razorpay-order`, { amount: orderAmount * 100 }, { headers: { Authorization: `Bearer ${token}` } });
        const { order_id, razorpay_key } = razorpayOrderRes.data;

        const options = {
          key: razorpay_key,
          amount: orderAmount * 100,
          currency: 'INR',
          name: 'SR Daily Nutrition',
          description: 'Order Payment',
          order_id: order_id,
          handler: async function (response) {
            try {
              await axios.post(`${API}/orders`, {
                name: formData.name,
                billing_address: formData.billingAddress,
                pincode: formData.pincode,
                phone: formData.phone,
                custom_items: updatedCartItems,
                payment_method: 'online',
                total_amount: orderAmount,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              }, { headers: { Authorization: `Bearer ${token}` } });
              localStorage.removeItem('customDesigns'); // Clean up after successful order
              toast.success('Order placed successfully! 🎉');
              navigate('/profile');
            } catch (error) {
              toast.error('Order creation failed after payment.');
            }
          },
          prefill: { name: user?.name, email: user?.email, contact: formData.phone },
          theme: { color: '#E31E24' }
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        await axios.post(`${API}/orders`, {
          name: formData.name,
          billing_address: formData.billingAddress,
          pincode: formData.pincode,
          phone: formData.phone,
          custom_items: updatedCartItems,
          payment_method: 'cod',
          total_amount: getTotal(),
        }, { headers: { Authorization: `Bearer ${token}` } });
        localStorage.removeItem('customDesigns'); // Clean up after successful order
        toast.success('Order placed with Cash on Delivery! 🎉');
        navigate('/profile');
      }
    } catch (error) {
      toast.error('Order initiation failed. Please try again.');
      console.error('Checkout error:', error);
    } finally {
      setIsProcessing(false);
      setUploadingImages(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white font-sans">
      {/* Sidebar */}
      <aside className={`
                fixed top-0 left-0 z-50
                w-64 bg-[#111114] text-gray-400 flex flex-col
                h-screen
                transform transition-transform duration-300 ease-in-out
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
        <div className="flex flex-col h-full">
          <div className="p-6 flex-1 overflow-y-auto">
            <div className="flex items-center gap-3 mb-10 px-2">
              <img src={logo} alt="Illustra" className="h-8 w-auto brightness-0 invert" />
            </div>

            <nav className="space-y-2">
              <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Menu</p>
              <Link to="/products" className="w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group hover:bg-[#1F1F23] hover:text-white">
                <Squares2X2Icon className="h-5 w-5 mr-3" />
                <span className="font-medium">All Products</span>
              </Link>

              <div className="pt-4">
                <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Categories</p>
                {categories.map((category) => (
                  <Link key={category.id} to={`/products?category_id=${category.id}`} className="w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group hover:bg-[#1F1F23] hover:text-white">
                    <TagIcon className="h-5 w-5 mr-3" />
                    <span className="font-medium">{category.name}</span>
                  </Link>
                ))}
              </div>

              <div className="pt-4 mt-4 border-t border-gray-800">
                <p className="px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Main Site</p>
                <Link to="/" className="w-full flex items-center px-4 py-3 text-gray-400 hover:text-white hover:bg-[#1F1F23] rounded-xl transition-all">
                  <HomeIcon className="h-5 w-5 mr-3" />
                  <span className="font-medium">Home</span>
                </Link>
              </div>
            </nav>
          </div>

          {/* User Profile Section at Bottom */}
          {user && (
            <div className="p-4 border-t border-gray-800 bg-[#0c0c0e]">
              <div className="relative">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-[#1F1F23] transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center text-yellow-900 font-bold text-sm shrink-0">
                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div className="flex-1 text-left overflow-hidden">
                    <p className="text-sm font-medium text-white truncate">{user.name || 'User'}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                  <ChevronUpIcon className={`h-5 w-5 text-gray-500 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {isProfileOpen && (
                  <div className="absolute bottom-full left-0 w-full mb-2 bg-[#1F1F23] border border-gray-800 rounded-xl shadow-xl overflow-hidden p-1">
                    <Link
                      to="/profile"
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-300 hover:bg-[#2a2a2f] hover:text-white rounded-lg transition-colors"
                    >
                      <UserIcon className="h-5 w-5" />
                      Profile
                    </Link>
                    <button
                      onClick={logout}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-red-400 hover:bg-[#2a2a2f] hover:text-red-300 rounded-lg transition-colors"
                    >
                      <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 overflow-x-hidden lg:ml-64">
        <header className="h-20 bg-white border-b border-gray-100 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden p-2 hover:bg-gray-100 rounded-xl text-gray-600">
              <Bars3Icon className="h-6 w-6" />
            </button>
            <div className="hidden md:flex items-center text-sm text-gray-500">
              <Link to="/products" className="hover:text-[#E31E24] transition-colors">Products</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-900 font-medium">Cart</span>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-6">
            <form onSubmit={handleSearch} className="hidden md:flex items-center bg-gray-100 rounded-full px-4 py-2 w-64 focus-within:ring-2 focus-within:ring-[#E31E24] focus-within:bg-white transition-all">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                className="bg-transparent border-none focus:ring-0 text-sm w-full ml-2 text-gray-900 placeholder-gray-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>

            <div className="flex items-center gap-3">
              <button className="p-2 hover:bg-gray-100 rounded-full text-gray-600 relative">
                <HeartIcon className="h-6 w-6" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
              </button>
              <Link to="/cart" className="p-2 hover:bg-gray-100 rounded-full text-gray-900 relative">
                <ShoppingBagIcon className="h-6 w-6" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#E31E24] text-white text-[10px] font-bold h-5 w-5 flex items-center justify-center rounded-full border-2 border-white">
                    {cart.length}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl font-bold text-gray-900 mb-8">
            Shopping Cart
          </motion.h1>

          {cart.length === 0 ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-12 text-center max-w-2xl mx-auto">
              <div className="w-24 h-24 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShoppingCartIcon className="h-10 w-10 text-[#E31E24]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
              <p className="text-gray-500 mb-8 text-lg">Looks like you haven't added anything to your cart yet.</p>
              <Link to="/products" className="inline-flex items-center gap-2 bg-[#111114] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#2a2a2f] transition-all shadow-lg hover:shadow-xl">
                <span>Start Shopping</span>
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
            </motion.div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left Column: Cart Items & Checkout Forms */}
              <div className="lg:col-span-2 space-y-8">
                {/* Cart Items */}
                <div className="space-y-4">
                  {cart.map((item, index) => {
                    // Load custom designs from localStorage
                    const customDesigns = JSON.parse(localStorage.getItem('customDesigns') || '{}');
                    const itemDesigns = customDesigns[item.id] || [];

                    return (
                      <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex gap-4">
                          <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-xl border border-gray-100">
                            <img src={item.product_image || "/default.jpg"} alt={item.product_title} className="h-full w-full object-cover object-center" />
                          </div>
                          <div className="flex flex-1 flex-col justify-between">
                            <div>
                              <div className="flex justify-between items-start">
                                <h3 className="text-base font-bold text-gray-900 line-clamp-1">{item.product_title || "Custom Product"}</h3>
                                <button onClick={() => removeFromCart(item.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1">
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                              <p className="mt-1 text-sm text-gray-500">Size: {item.size || 'M'}</p>
                              {itemDesigns.length > 0 && (
                                <p className="mt-1 text-xs text-green-600 font-medium flex items-center gap-1">
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  {itemDesigns.length} page(s) customized
                                </p>
                              )}
                            </div>
                            <div className="flex items-end justify-between">
                              <div className="flex items-center border border-gray-200 rounded-lg">
                                <button className="p-1 hover:bg-gray-50 rounded-l-lg text-gray-600" onClick={() => updateCartItemQuantity(item.id, Math.max(1, item.quantity - 1))} disabled={item.quantity <= 1}>
                                  <MinusIcon className="h-4 w-4" />
                                </button>
                                <span className="w-8 text-center text-sm font-medium text-gray-900">{item.quantity}</span>
                                <button className="p-1 hover:bg-gray-50 rounded-r-lg text-gray-600" onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}>
                                  <PlusIcon className="h-4 w-4" />
                                </button>
                              </div>
                              <p className="text-lg font-bold text-[#E31E24]">₹{item.product_price * item.quantity}</p>
                            </div>
                          </div>
                        </div>

                        {/* Display Designed Pages */}
                        {itemDesigns.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <h4 className="text-sm font-bold text-gray-900 mb-3">Your Custom Design:</h4>
                            <div className="flex gap-3 overflow-x-auto pb-2">
                              {itemDesigns.map((design, idx) => (
                                <div key={idx} className="flex-shrink-0">
                                  <div className="relative group">
                                    <img
                                      src={design.imageUrl || design.imageData}
                                      alt={design.pageName}
                                      className="h-32 w-auto rounded-lg border-2 border-gray-200 hover:border-[#E31E24] transition-colors shadow-sm"
                                    />
                                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs py-1 px-2 rounded-b-lg">
                                      {design.pageName}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )
                  })}
                </div>

                {/* Checkout Forms (Only if logged in) */}
                {user ? (
                  <>
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-6">Billing Information</h2>
                      <div className="space-y-4">
                        <input type="text" placeholder="Full Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E31E24] focus:ring-0 transition-colors" />
                        <textarea placeholder="Full Address" rows="3" value={formData.billingAddress} onChange={e => setFormData({ ...formData, billingAddress: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E31E24] focus:ring-0 transition-colors" />
                        <input type="text" placeholder="Pincode" value={formData.pincode} onChange={e => setFormData({ ...formData, pincode: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E31E24] focus:ring-0 transition-colors" />
                        <input type="tel" placeholder="Phone Number" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E31E24] focus:ring-0 transition-colors" />
                      </div>
                    </div>

                    {/* Customization */}
                    {cart.some((item, index) => products[index]?.is_customizable) && (
                      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                          <PhotoIcon className="h-6 w-6 text-[#E31E24]" />
                          Product Customization
                        </h2>
                        <div className="space-y-6">
                          {cart.map((item, index) => {
                            const product = products[index];
                            if (!product?.is_customizable) return null;
                            return (
                              <div key={item.id} className="border border-gray-100 rounded-xl p-4">
                                <div className="flex items-center gap-4 mb-4">
                                  <img src={product.images[0]} alt={product.title} className="w-12 h-12 rounded-lg object-cover" />
                                  <div>
                                    <h3 className="font-medium text-gray-900">{product.title}</h3>
                                    <p className="text-sm text-gray-500">Size: {item.size}</p>
                                  </div>
                                </div>
                                {customImages[item.id] ? (
                                  <div className="relative rounded-xl overflow-hidden border border-gray-200">
                                    <img src={customImages[item.id]} alt="Custom" className="w-full h-48 object-cover" />
                                    <button onClick={() => removeCustomImage(item.id)} className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-lg hover:bg-red-600">
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-[#E31E24] transition-colors">
                                    <CloudArrowUpIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500 mb-4">Upload your design</p>
                                    <input type="file" accept="image/*" className="hidden" id={`custom-${item.id}`} onChange={e => handleCustomImageUpload(item.id, e.target.files[0])} />
                                    <label htmlFor={`custom-${item.id}`} className="inline-block px-4 py-2 bg-[#E31E24] text-white rounded-lg text-sm font-bold cursor-pointer hover:bg-[#C01A1F]">Choose File</label>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-6">Shipping & Payment</h2>
                      <div className="space-y-6">
                        <div>
                          <label className="text-sm font-bold text-gray-900 mb-3 block">Shipping Method</label>
                          <div className="grid grid-cols-1 gap-3">
                            {[
                              { id: 'standard', name: 'Standard Delivery', price: `₹${getShippingCost()}`, time: '5-7 days' }
                            ].map(opt => (
                              <div key={opt.id} onClick={() => setFormData({ ...formData, shippingOption: opt.id })} className={`cursor-pointer border rounded-xl p-3 transition-all ${formData.shippingOption === opt.id ? 'border-[#E31E24] bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                <div className="font-bold text-sm text-gray-900">{opt.name}</div>
                                <div className="text-xs text-gray-500">{opt.time}</div>
                                <div className="font-bold text-xs text-[#E31E24] mt-1">{opt.price}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-bold text-gray-900 mb-3 block">Payment Method</label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {/* <div onClick={() => setFormData({ ...formData, paymentMethod: 'online' })} className={`cursor-pointer border rounded-xl p-4 flex items-center gap-3 transition-all ${formData.paymentMethod === 'online' ? 'border-[#E31E24] bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                              <CreditCardIcon className="h-6 w-6 text-blue-600" />
                              <div>
                                <div className="font-bold text-sm text-gray-900">Online Payment</div>
                                <div className="text-xs text-gray-500">UPI, Cards, Netbanking</div>
                              </div>
                            </div> */}
                            <div onClick={() => setFormData({ ...formData, paymentMethod: 'cod' })} className={`cursor-pointer border rounded-xl p-4 flex items-center gap-3 transition-all ${formData.paymentMethod === 'cod' ? 'border-[#E31E24] bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                              <TruckIcon className="h-6 w-6 text-green-600" />
                              <div>
                                <div className="font-bold text-sm text-gray-900">Cash on Delivery</div>
                                <div className="text-xs text-gray-500">Pay upon arrival</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-orange-50 border border-orange-100 rounded-3xl p-8 text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Login to Checkout</h3>
                    <p className="text-gray-600 mb-6">Please sign in to your account to complete your order.</p>
                    <Link to="/login" className="inline-block bg-[#E31E24] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#C01A1F] transition-colors">
                      Sign In
                    </Link>
                  </div>
                )}
              </div>

              {/* Right Column: Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sticky top-24 w-full">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h2>

                  {/* Available Coupons */}
                  {adminCouponsList.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-bold text-gray-900 mb-3">Available Coupons</h3>
                      <div className="space-y-3">
                        {adminCouponsList.map(coupon => (
                          <div key={coupon.id} className="border border-dashed border-[#E31E24]/30 rounded-xl p-3 bg-orange-50/50 flex justify-between items-center group hover:bg-orange-50 transition-colors">
                            <div>
                              <div className="font-bold text-sm text-[#E31E24] flex items-center gap-2">
                                <TagIcon className="h-4 w-4" />
                                {coupon.coupon_name}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {coupon.type === 'percentage' ? `${coupon.discount}% OFF` : `₹${coupon.discount} OFF`}
                                {coupon.min_purchase > 0 && ` • Min purchase: ₹${coupon.min_purchase}`}
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                const subtotal = getSubtotal();
                                if (coupon.min_purchase && subtotal < Number(coupon.min_purchase)) {
                                  return toast.error(`Minimum purchase of ₹${coupon.min_purchase} required`);
                                }
                                setFormData({ ...formData, couponCode: coupon.coupon_name });
                                toast.success('Coupon code applied!');
                              }}
                              className="text-xs font-bold bg-white border border-gray-200 px-3 py-1.5 rounded-lg hover:border-[#E31E24] hover:text-[#E31E24] transition-all shadow-sm"
                            >
                              Apply
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Coupon Input */}
                  <div className="mb-6">
                    <div className="flex gap-2 mb-2">
                      <input type="text" placeholder="Coupon Code" value={formData.couponCode} onChange={e => setFormData({ ...formData, couponCode: e.target.value })} className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#E31E24] focus:ring-0" />
                      <button onClick={() => {
                        if (!formData.couponCode) return toast.error('Enter code');
                        const c = allCoupons.find(x => x.coupon_name === formData.couponCode);
                        if (!c) return toast.error('Invalid coupon');

                        const subtotal = getSubtotal();
                        if (c.min_purchase && subtotal < Number(c.min_purchase)) {
                          return toast.error(`Minimum purchase of ₹${c.min_purchase} required`);
                        }

                        toast.success('Coupon applied!');
                      }} className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-800">Apply</button>
                    </div>
                    {appliedCoupon && getCouponDiscount() > 0 && <div className="text-xs text-green-600 font-bold">Coupon applied: {appliedCoupon.coupon_name}</div>}
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal</span>
                      <span className="font-medium text-gray-900">₹{getSubtotal()}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Shipping</span>
                      <span className="text-green-600 font-medium">{getShippingCost() === 0 ? 'Free' : `₹${getShippingCost()}`}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Tax (18%)</span>
                      <span className="font-medium text-gray-900">₹{getTax()}</span>
                    </div>
                    {getCouponDiscount() > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount</span>
                        <span className="font-bold">-₹{getCouponDiscount()}</span>
                      </div>
                    )}
                    <div className="border-t border-gray-100 pt-4 mt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-gray-900">Total</span>
                        <span className="text-2xl font-bold text-[#E31E24]">₹{getTotal()}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handlePlaceOrder}
                    disabled={!user || isProcessing}
                    className="w-full mt-8 bg-[#111114] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#2a2a2f] transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <>
                        <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <span>Place Order</span>
                        <ArrowRightIcon className="h-5 w-5" />
                      </>
                    )}
                  </button>

                  <div className="mt-6 space-y-3">
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <ShieldCheckIcon className="h-5 w-5 text-green-500" />
                      <span>Secure checkout</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <TruckIcon className="h-5 w-5 text-blue-500" />
                      <span>Free shipping on orders over ₹500</span>
                    </div>
                  </div>
                </div>


                {/* Related Products Sidebar */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 mt-8 overflow-hidden">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">You May Also Like</h2>
                  <div className="grid grid-cols-1 gap-4">
                    {relatedProducts.map((related) => (
                      <div key={related.id} className="group relative border border-gray-100 rounded-xl p-3 flex gap-3 hover:border-[#E31E24] transition-colors cursor-pointer" onClick={() => navigate(`/product/${related.id}`)}>
                        <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                          <img
                            src={related.images?.[0] || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab'}
                            alt={related.title}
                            className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="flex flex-1 flex-col justify-center">
                          <h3 className="text-sm font-bold text-gray-900 line-clamp-2 group-hover:text-[#E31E24] transition-colors">
                            {related.title}
                          </h3>
                          <p className="mt-1 text-sm font-medium text-gray-500">₹{related.price}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm" onClick={() => setIsSidebarOpen(false)} />
      )}
    </div>
  );
};

// Enhanced Checkout Page
const CheckoutPage = () => {
  // Coupons state must be declared before use
  const [allCoupons, setAllCoupons] = useState([]);
  // Admin coupons only (for listing)
  const adminCouponsList = allCoupons.filter(c => c.coupon_user === 'Admin' && c.status === 'active');
  // Coupons from admin
  const [adminCoupons, setAdminCoupons] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    billingAddress: '',
    phone: '',
    couponCode: '',
    shippingOption: 'standard',
    paymentMethod: 'cod', // 'online' or 'cod'
    pincode: '',
  });

  useEffect(() => {
    // Fetch coupons from backend
    const fetchCoupons = async () => {
      try {
        const response = await axios.get(`${API}/coupons`);
        // Only show coupons from admin user and active status
        const filtered = (response.data || []).filter(c => c.coupon_user === 'Admin' && c.status === 'active');
        setAdminCoupons(filtered);
      } catch (error) {
        setAdminCoupons([]);
      }
    };
    fetchCoupons();
  }, []);

  // Helper to get applied coupon object
  useEffect(() => {
    // Fetch all coupons from backend
    const fetchCoupons = async () => {
      try {
        const response = await axios.get(`${API}/coupons`);
        setAllCoupons(response.data || []);
      } catch (error) {
        setAllCoupons([]);
      }
    };
    fetchCoupons();
  }, []);

  const appliedCoupon = allCoupons.find(c => c.coupon_name === formData.couponCode);

  // Calculate discount
  const getCouponDiscount = () => {
    if (!appliedCoupon) return 0;
    const subtotal = getTotalPrice();
    // Minimum order value validation
    if (
      typeof appliedCoupon.min_purchase !== 'undefined' &&
      subtotal < Number(appliedCoupon.min_purchase)
    ) {
      return 0; // Do not apply coupon if subtotal is less than minimum order value
    }
    if (appliedCoupon.type === 'percentage') {
      return Math.round(subtotal * (appliedCoupon.discount / 100));
    } else {
      return Math.round(appliedCoupon.discount);
    }
  };

  // Helper to get product by id
  // Helper to get product by id (support both id and _id)
  const getProductById = (productId) => products.find(p => p.id === productId || p._id === productId);
  const { cart, user } = useAppContext();
  const [products, setProducts] = useState([]);
  const [customImages, setCustomImages] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const navigate = useNavigate();

  // Razorpay script loader
  useEffect(() => {
    if (!window.Razorpay) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    fetchProductDetails();
  }, [cart]);

  const fetchProductDetails = async () => {
    try {
      // Get unique product IDs from cart
      const productIds = [...new Set(cart.map(item => item.product_id))];
      const productPromises = productIds.map(id => axios.get(`${API}/products/${id}`));
      const responses = await Promise.all(productPromises);
      setProducts(responses.map(response => response.data));
    } catch (error) {
      console.error('Failed to fetch product details:', error);
    }
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => {
      return total + (item.product_price * item.quantity);
    }, 0);
  };


  const handleCustomImageUpload = (cartItemId, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCustomImages(prev => ({
          ...prev,
          [cartItemId]: e.target.result
        }));
        toast.success('Custom image uploaded! 🎨');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCustomImage = (cartItemId) => {
    setCustomImages(prev => {
      const newImages = { ...prev };
      delete newImages[cartItemId];
      return newImages;
    });
    toast.success('Custom image removed');
  };

  const uploadCustomImagesToServer = async () => {
    const uploadPromises = Object.entries(customImages).map(async ([cartItemId, imageData]) => {
      try {
        const response = await fetch(imageData);
        const blob = await response.blob();
        const formData = new FormData();
        formData.append('file', blob, `custom-${cartItemId}.jpg`);
        formData.append('folder', 'custom');
        const token = localStorage.getItem('token');
        const uploadResponse = await axios.post(`${API}/upload-image`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        return { cartItemId, imageUrl: uploadResponse.data.image_url };
      } catch (error) {
        console.error('Failed to upload custom image:', error);
        return { cartItemId, imageUrl: imageData };
      }
    });
    return await Promise.all(uploadPromises);
  };

  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();
    if (!formData.pincode || formData.pincode.trim() === '') {
      toast.error('Please enter your pincode');
      return;
    }
    setIsProcessing(true);
    setUploadingImages(true);
    const token = localStorage.getItem('token');
    try {
      const uploadedImages = await uploadCustomImagesToServer();
      const updatedCartItems = cart.map(item => {
        const customImage = uploadedImages.find(img => img.cartItemId === item.id);
        return { ...item, custom_image_url: customImage?.imageUrl || null };
      });
      if (formData.paymentMethod === 'online') {
        const orderAmount = getTotalPrice() + Math.round(getTotalPrice() * 0.18);
        const razorpayOrderRes = await axios.post(`${API}/create-razorpay-order`, { amount: orderAmount * 100 }, { headers: { Authorization: `Bearer ${token}` } });
        const { order_id, razorpay_key } = razorpayOrderRes.data;
        const options = {
          key: razorpay_key,
          amount: orderAmount * 100,
          currency: 'INR',
          name: 'SR Daily Nutrition',
          description: 'Order Payment',
          order_id: order_id,
          handler: async function (response) {
            try {
              await axios.post(`${API}/orders`, {
                name: formData.name,
                billing_address: formData.billingAddress,
                pincode: formData.pincode,
                phone: formData.phone,
                custom_items: updatedCartItems,
                payment_method: 'online',
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              }, { headers: { Authorization: `Bearer ${token}` } });
              toast.success('Order placed successfully! 🎉', { style: { borderRadius: '12px', background: '#10b981', color: '#fff' } });
              navigate('/profile');
            } catch (error) {
              toast.error('Order creation failed after payment. Please contact support.');
            }
          },
          prefill: { name: user?.name, email: user?.email, contact: formData.phone },
          theme: { color: '#E31E24' }
        };
        const rzp = new window.Razorpay(options);

        rzp.open();
      } else {
        // COD: Place order directly
        await axios.post(`${API}/orders`, {
          name: formData.name,
          billing_address: formData.billingAddress,
          pincode: formData.pincode,
          phone: formData.phone,
          custom_items: updatedCartItems,
          payment_method: 'cod',
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Order placed with Cash on Delivery! 🎉', { style: { borderRadius: '12px', background: '#10b981', color: '#fff' } });
        navigate('/profile');
      }
    } catch (error) {
      toast.error('Order initiation failed. Please try again.');
      console.error('Checkout error:', error);
    } finally {
      setIsProcessing(false);
      setUploadingImages(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-12 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4 text-lg">Please login to proceed with checkout</p>
          <Link to="/login" className="bg-[#E31E24] text-white px-6 py-3 rounded-lg hover:bg-[#C01A1F] transition-colors">Login</Link>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-12 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4 text-lg">Your cart is empty</p>
          <Link to="/products" className="bg-[#E31E24] text-white px-6 py-3 rounded-lg hover:bg-[#C01A1F] transition-colors">Continue Shopping</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl font-bold text-[#362222] mb-8">Checkout</motion.h1>
        <form onSubmit={handleCheckoutSubmit} className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Billing Information */}
            {/* Coupon Selection */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl shadow-lg p-8 mb-8">
              <h2 className="text-xl font-semibold mb-4">Apply Coupon</h2>
              {/* Admin coupon quick apply */}
              {adminCouponsList.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">Available Admin Coupons</h3>
                  <div className="space-y-2">
                    {adminCouponsList.map(coupon => (
                      <div key={coupon.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                        <div>
                          <div className="font-bold text-[#E31E24]">{coupon.coupon_name}</div>
                          <div className="text-sm text-gray-700">{coupon.type === 'percentage' ? `${coupon.discount}% off` : `₹${coupon.discount} off`} | Expires: {coupon.valid_to ? coupon.valid_to.split('T')[0] : 'N/A'}</div>
                        </div>
                        <button type="button" className="bg-[#E31E24] text-white px-3 py-1 rounded hover:bg-[#C01A1F]" onClick={() => {
                          const minPurchase = Number(coupon.min_purchase || 0);
                          if (minPurchase > 0 && getTotalPrice() < minPurchase) {
                            return toast.error(`Minimum order value of ₹${minPurchase} required.`);
                          }
                          setFormData({ ...formData, couponCode: coupon.coupon_name });
                          toast.success('Coupon applied!');
                        }}>
                          Apply
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Manual coupon entry */}
              <div className="space-y-3">
                <input
                  type="text"
                  value={formData.couponCode}
                  onChange={e => setFormData({ ...formData, couponCode: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24]"
                  placeholder="Enter coupon code"
                />
                <button
                  type="button"
                  className="bg-[#E31E24] text-white px-4 py-2 rounded hover:bg-[#C01A1F] mt-2"
                  onClick={() => {
                    if (!formData.couponCode) {
                      toast.error('Please enter a coupon code');
                      return;
                    }
                    const coupon = allCoupons.find(c => c.coupon_name === formData.couponCode);
                    if (!coupon) {
                      toast.error('Invalid or expired coupon');
                      return;
                    }
                    // Minimum order value enforcement
                    const minOrderValue = Number(coupon.min_purchase || 0);
                    const cartTotal = getTotalPrice();
                    if (minOrderValue > 0 && cartTotal < minOrderValue) {
                      toast.error(`Minimum order value of ₹${minOrderValue} required for this coupon.`);
                      return;
                    }
                    toast.success('Coupon applied!');
                  }}
                >
                  Apply Coupon
                </button>
              </div>
              {/* Applied coupon message - only show if coupon is valid and applicable */}
              {formData.couponCode && appliedCoupon &&
                (typeof appliedCoupon.min_purchase === 'undefined' || getTotalPrice() >= Number(appliedCoupon.min_purchase)) && (
                  <div className="mt-4 text-green-700 font-semibold">Applied Coupon: {formData.couponCode}</div>
                )}
              {/* Invalid coupon message */}
              {formData.couponCode && !appliedCoupon && (
                <div className="mt-4 text-red-600 font-semibold">Invalid or expired coupon</div>
              )}
              {/* Minimum order value error message */}
              {formData.couponCode && appliedCoupon && typeof appliedCoupon.min_purchase !== 'undefined' && getTotalPrice() < Number(appliedCoupon.min_purchase) && (
                <div className="font-semibold mt-4 text-[#E31E24]">Coupon cannot be applied. Minimum order value is ₹{appliedCoupon.min_purchase}.</div>
              )}
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold mb-6">Billing Information</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24]"
                    placeholder="Enter your name..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Address</label>
                  <textarea
                    rows="3"
                    value={formData.billingAddress}
                    onChange={e => setFormData({ ...formData, billingAddress: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24]"
                    placeholder="Enter your complete address..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                  <input
                    type="text"
                    required
                    value={formData.pincode}
                    onChange={e => setFormData({ ...formData, pincode: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24]"
                    placeholder="Enter pincode..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24]"
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>
            </motion.div>
            {/* Product Customization Section */}
            {cart.some((item, index) => products[index]?.is_customizable) && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-3xl shadow-lg p-8">
                <h2 className="text-2xl font-semibold mb-6 flex items-center"><PhotoIcon className="h-7 w-7 text-[#E31E24] mr-3" />Product Customization</h2>
                <p className="text-gray-600 mb-6">Upload your custom designs for the products that support customization.</p>
                <div className="space-y-6">
                  {cart.map((item, index) => {
                    const product = products[index];
                    if (!product?.is_customizable) return null;
                    return (
                      <div key={item.id} className="border border-gray-200 p-6">
                        <div className="flex items-start space-x-4 mb-4">
                          <img src={product.images[0] || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab'} alt={product.title} className="w-16 h-16 object-cover rounded-lg" />
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-800">{product.title}</h3>
                            <p className="text-sm text-gray-600">Size: {item.size || 'Standard'}</p>
                            <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                          </div>
                          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">Customizable</div>
                        </div>
                        {customImages[item.id] ? (
                          <div className="relative">
                            <img src={customImages[item.id]} alt="Custom design" className="w-full h-48 object-cover border border-gray-200" />
                            <button type="button" onClick={() => removeCustomImage(item.id)} className="absolute top-2 right-2 bg-red-500 text-white p-2 hover:bg-red-600 transition-colors"><XMarkIcon className="h-4 w-4" /></button>
                            <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white px-3 py-1 text-sm">Custom Design Uploaded ✨</div>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#E31E24] transition-colors">
                            <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <div className="space-y-2">
                              <p className="text-gray-600">Upload your custom design for this product</p>
                              <input type="file" accept="image/*" className="hidden" id={`custom-image-${item.id}`} onChange={e => handleCustomImageUpload(item.id, e.target.files[0])} />
                              <label htmlFor={`custom-image-${item.id}`} className="inline-flex items-center px-4 py-2 bg-[#E31E24] text-white rounded-lg hover:bg-[#C01A1F] cursor-pointer transition-colors"><PhotoIcon className="h-5 w-5 mr-2" />Choose Design</label>
                            </div>
                            <p className="text-xs text-gray-500 mt-3">Recommended: 300 DPI, PNG/JPG format, up to 10MB</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-100 p-2 rounded-lg"><PhotoIcon className="h-5 w-5 text-blue-600" /></div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-1">Customization Guidelines</h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• High resolution images (300 DPI) for best print quality</li>
                        <li>• CMYK color mode preferred for accurate color reproduction</li>
                        <li>• Add 3mm bleed area around your design if edge-to-edge printing</li>
                        <li>• Our design team will review and optimize your files before printing</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            {/* Shipping Options */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold mb-6">Shipping Options</h2>
              <div className="space-y-4">
                {[
                  { id: 'standard', name: 'Standard Delivery', time: '5-7 business days', price: 'Free' },
                  { id: 'express', name: 'Express Delivery', time: '2-3 business days', price: '₹99' },
                  { id: 'overnight', name: 'Overnight Delivery', time: 'Next business day', price: '₹299' }
                ].map(option => (
                  <label key={option.id} className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-[#E31E24] cursor-pointer transition-colors">
                    <input type="radio" name="shipping" value={option.id} checked={formData.shippingOption === option.id} onChange={e => setFormData({ ...formData, shippingOption: e.target.value })} className="text-[#E31E24] focus:ring-[#E31E24]" />
                    <div className="ml-4 flex-1">
                      <div className="font-medium">{option.name}</div>
                      <div className="text-sm text-gray-600">{option.time}</div>
                    </div>
                    <div className="font-semibold text-[#E31E24]">{option.price}</div>
                  </label>
                ))}
              </div>
            </motion.div>
            {/* Payment Method */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold mb-6">Payment Method</h2>
              <div className="space-y-4">
                {/* <label className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-[#E31E24] cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="online"
                    checked={formData.paymentMethod === 'online'}
                    onChange={e => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="text-[#E31E24] focus:ring-[#E31E24]"
                  />
                  <div className="ml-4 flex-1">
                    <div className="font-medium">Online Payment</div>
                    <div className="text-sm text-gray-600">Pay securely via Razorpay (UPI, Card, Netbanking)</div>
                  </div>
                  <CreditCardIcon className="h-6 w-6 text-blue-600" />
                </label> */}
                <label className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-[#E31E24] cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="cod"
                    checked={formData.paymentMethod === 'cod'}
                    onChange={e => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="text-[#E31E24] focus:ring-[#E31E24]"
                  />
                  <div className="ml-4 flex-1">
                    <div className="font-medium">Cash on Delivery (COD)</div>
                    <div className="text-sm text-gray-600">Pay when your order arrives at your doorstep</div>
                  </div>
                  <TruckIcon className="h-6 w-6 text-green-600" />
                </label>
              </div>
            </motion.div>
          </div>
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-3xl shadow-lg p-8 sticky top-8">
              <h2 className="text-2xl font-semibold mb-6">Order Summary</h2>
              <div className="space-y-4 mb-6">
                {cart.map((item) => {
                  const product = getProductById(item.product_id);
                  // Defensive fallback for images
                  let imageSrc = customImages[item.id];
                  if (!imageSrc) {
                    if (product && Array.isArray(product.images) && product.images.length > 0 && product.images[0] && !product.images[0].includes('default')) {
                      imageSrc = product.images[0];
                    } else if (item.product_image) {
                      imageSrc = item.product_image;
                    } else {
                      imageSrc = "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab";
                    }
                  }
                  // Use product.price if available, else fallback to item.product_price
                  const priceEach = product?.price || item.product_price;
                  return (
                    <div key={item.id} className="flex items-start space-x-3 p-3 border border-gray-100 rounded-lg">
                      <div className="relative">
                        <img src={imageSrc} alt={product?.title || "Product"} className="w-16 h-16 object-cover rounded-lg" />
                        {customImages[item.id] && (<div className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">Custom</div>)}
                        {product?.is_customizable && !customImages[item.id] && (<div className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">⚠️</div>)}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{product?.title || item.product_title || "Custom Product"}</div>
                        <div className="text-xs text-gray-600">{item.size && `Size: ${item.size} • `}Qty: {item.quantity}</div>
                        {product?.is_customizable && !customImages[item.id] && (<div className="text-xs text-orange-600 mt-1">⚠️ Custom design pending</div>)}
                        {customImages[item.id] && (<div className="text-xs text-green-600 mt-1">✨ Custom design uploaded</div>)}
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">₹{priceEach * item.quantity}</div>
                        <div className="text-xs text-gray-500">₹{priceEach} each</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="space-y-4 border-t pt-6">
                <div className="flex justify-between"><span>Subtotal</span><span>₹{getTotalPrice()}</span></div>
                {(() => {
                  let shippingLabel = 'Free';
                  let shippingCharge = 0;
                  if (formData.shippingOption === 'express') {
                    shippingLabel = '₹99';
                    shippingCharge = 99;
                  } else if (formData.shippingOption === 'overnight') {
                    shippingLabel = '₹299';
                    shippingCharge = 299;
                  }
                  return <div className="flex justify-between"><span>Shipping</span><span>{shippingLabel}</span></div>;
                })()}
                {/* Tax removed as per request */}
                {appliedCoupon && (
                  <div className="flex justify-between text-green-700 font-semibold"><span>Coupon Discount ({appliedCoupon.coupon_name})</span><span>-₹{getCouponDiscount()}</span></div>
                )}
                {(() => {
                  let shippingCharge = 0;
                  if (formData.shippingOption === 'express') shippingCharge = 99;
                  else if (formData.shippingOption === 'overnight') shippingCharge = 299;
                  const total = getTotalPrice() + shippingCharge - getCouponDiscount();
                  return <div className="border-t pt-4"><div className="flex justify-between font-bold text-xl"><span>Total</span><span>₹{total}</span></div></div>;
                })()}
              </div>
              <motion.button type="submit" disabled={isProcessing} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full bg-gradient-to-r from-[#E31E24] to-[#C01A1F] text-white py-4 rounded-xl text-lg font-semibold hover:from-[#C01A1F] hover:to-[#A01619] transition-all duration-300 shadow-lg mt-8 disabled:opacity-50">
                {isProcessing ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    <span>{uploadingImages ? 'Uploading custom designs...' : 'Processing order...'}</span>
                  </div>
                ) : (
                  'Place Order'
                )}
              </motion.button>
            </motion.div>
          </div>
        </form>
      </div>
    </div>
  );
}
// ProfilePage component moved to pages/ProfilePage.js

const LoginPage = () => {
  const { login } = useAppContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await login(email, password);
    if (success) {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-12 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 mx-4"
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-[#362222]">Welcome Back</h2>
          <p className="mt-2 text-gray-600">Sign in to your account</p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24]"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24]"
              placeholder="Enter your password"
            />
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-gradient-to-r from-[#E31E24] to-[#C01A1F] text-white py-3 rounded-xl font-semibold hover:from-[#C01A1F] hover:to-[#A01619] transition-all duration-300 shadow-lg"
          >
            Sign In
          </motion.button>
        </form>

        <p className="mt-8 text-center text-gray-600">
          Don't have an account?{' '}
          <Link to="/register" className="font-medium text-[#E31E24] hover:text-[#C01A1F] transition-colors">
            Sign up
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

const RegisterPage = () => {
  const { register } = useAppContext();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: ''
  });
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await register(formData);
    if (success) {
      navigate('/');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-12 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 mx-4"
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-[#362222]">Create Account</h2>
          <p className="mt-2 text-gray-600">Join the SR Daily Nutrition community</p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
            <input
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24]"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24]"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              name="password"
              type="password"
              required
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24]"
              placeholder="Create a password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
            <input
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24]"
              placeholder="+91 98765 43210"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            <textarea
              name="address"
              rows="2"
              value={formData.address}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24]"
              placeholder="Enter your address"
            />
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-gradient-to-r from-[#E31E24] to-[#C01A1F] text-white py-3 rounded-xl font-semibold hover:from-[#C01A1F] hover:to-[#A01619] transition-all duration-300 shadow-lg"
          >
            Create Account
          </motion.button>
        </form>

        <p className="mt-8 text-center text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-[#E31E24] hover:text-[#C01A1F] transition-colors">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

// Admin Wrapper Component
// Main Layout Component to handle conditional navigation
const MainLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/customize');

  useEffect(() => {
    // Only track non-admin routes for analytics
    if (!isAdminRoute) {
      axios.post(`${API}/track-visit`, { path: location.pathname })
        .catch(() => { /* Silent fail */ });
    }
  }, [location.pathname, isAdminRoute]);

  return (
    <>
      {!isAdminRoute && <Navigation />}
      {children}
    </>
  );
};



// Main App Component
const App = () => {
  return (
    <AppProvider>
      <BrowserRouter>
        <Toaster position="top-center" />
        <MainLayout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/product/:id" element={<ProductDetailPage />} />
            <Route path="/customize/:productId" element={<ProductCustomizer />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:id" element={<BlogPostDetail />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/products/:productId/template" element={<TemplateBuilder />} />
          </Routes>
        </MainLayout>
      </BrowserRouter>
    </AppProvider>
  );
};

export default App;
